import { handleActions, combineActions } from 'redux-actions'
import { fromJS, List as ImmutableList } from 'immutable'
import { calculateRanking, calculateContactPerformance } from 'utils/contact_metrics'
import {
  applyFakeContactAttributes,
  mergeContactData,
  normalizeContact,
  normalizeContacts,
  revertFakeContactUpdate
} from 'utils/contact_management'

import * as Actions from 'actions/contact_management'
import * as AppActions from 'actions/app'

export const initialState = fromJS({
  contactDetailsData: {},
  contacts: [],
  contactsSearch: [],
  contactsTotal: 0,
  contactsFilterIds: [],
  distributionLists: [],
  distributionListsSearch: [],
  distributionListsTotal: 0,
  distributionListsFilterIds: [],
  newContactData: null,
  suggestedContactData: {},
  performance: {},
  ranking: [],
  selectedDistributionLists: [],
  distributionListFormData: {
    selectedLists: [],
    newListNames: []
  },
  distributionListDetailsData: {
    list: {
      contacts: []
    },
    selectedContactsToAdd: [],
    selectedContactsToRemove: [],
    contactsToRemove: []
  }
})

const getDistributionListsByIds = (state, ids) => {
  const selectedIds = fromJS(ids || []).map(id => String(id)).toSet()

  return state.get('distributionLists').filter(dl => selectedIds.includes(String(dl.get('id'))))
}

const getDistributionListId = distributionList => {
  const id = distributionList.get('id')

  return id === undefined || id === null ? null : String(id)
}

const mergeDistributionListsById = (currentLists, incomingLists) => {
  const indexesById = new Map()

  return currentLists
    .concat(fromJS(incomingLists || []))
    .reduce((mergedLists, distributionList) => {
      const id = getDistributionListId(distributionList)

      if (!id) {
        return mergedLists.push(distributionList)
      }

      if (!indexesById.has(id)) {
        indexesById.set(id, mergedLists.size)

        return mergedLists.push(distributionList)
      }

      return mergedLists.set(indexesById.get(id), distributionList)
    }, currentLists.clear())
}

export default handleActions(
  {
    [Actions.updateContacts]: (state, { payload }) => state
      .update('contacts', contacts => contacts.concat(normalizeContacts(payload))),

    [Actions.setTotalContacts]: (state, { payload }) => state
      .set('contactsTotal', payload),

    [Actions.clearSmartSearchFilterIds]: state => state
      .set('contactsFilterIds', initialState.get('contactsFilterIds'))
      .set('distributionListsFilterIds', initialState.get('distributionListsFilterIds')),
    [Actions.fetchSmartSearchContactIdsStart]: state => state
      .set('contactsFilterIds', initialState.get('contactsFilterIds')),
    [Actions.fetchSmartSearchContactIdsSuccess]: (state, { payload }) => state
      .set('contactsFilterIds', fromJS(payload || [])),
    [Actions.fetchSmartSearchDistributionListIdsStart]: state => state
      .set('distributionListsFilterIds', initialState.get('distributionListsFilterIds')),
    [Actions.fetchSmartSearchDistributionListIdsSuccess]: (state, { payload }) => state
      .set('distributionListsFilterIds', fromJS(payload || [])),

    [Actions.updateContactRanking]: state => {
      const ranking = calculateRanking(state.get('contacts'))
      const performance = calculateContactPerformance(state.get('contacts'))

      return state
        .set('ranking', ranking)
        .set('performance', performance)
    },

    [Actions.deleteContactsSuccess]: (state, { payload: idsToDelete }) => {
      // Remove deleted contacts from the contacts list
      const updatedContacts = state.get('contacts').filter(contact => !idsToDelete.includes(contact.get('id')))

      // Remove deleted contacts from each distribution list
      const updatedDistributionLists = state.get('distributionLists').map(
        dl => dl.update('contacts', contacts => contacts.filter(contact => !idsToDelete.includes(contact.get('id'))))
      )

      return state.merge({
        contacts: updatedContacts,
        contactsTotal: state.get('contactsTotal') - idsToDelete.length,
        distributionLists: updatedDistributionLists
      })
    },
    [Actions.updateDistributionLists]: (state, { payload }) => state
      .update('distributionLists', lists => mergeDistributionListsById(lists, payload)),

    [Actions.setTotalDistributionLists]: (state, { payload }) => state
      .set('distributionListsTotal', payload),

    // Contact Details Page
    [Actions.setDetailPageContactId]: (state, { payload }) => {
      const contact = state.get('contacts').find(c => c.get('id') === payload) || null

      if (!contact) {
        return state
      }

      return state.set('contactDetailsData', fromJS({ contact: normalizeContact(contact) }))
    },
    [Actions.switchToListContactDetails]: (state, { payload }) => {
      const stateContact = state.get('contacts').find(c => c.get('id') === payload.contactId) || null
      const currentDistributionList = state.getIn(['distributionListDetailsData', 'list'])
      const contact = mergeContactData(stateContact, null, currentDistributionList)

      if (!contact) {
        return state.set('contactDetailsData', initialState.get('contactDetailsData'))
      }

      return state.set('contactDetailsData', fromJS({ contact: normalizeContact(contact) }))
    },
    [Actions.switchToContactListDetails]: state => state,
    [Actions.fetchContactSuccess]: (state, { payload }) => {
      const currentContact = state.getIn(['contactDetailsData', 'contact'])
      const currentDistributionList = state.getIn(['distributionListDetailsData', 'list'])
      const mergedContact = mergeContactData(payload.contact, currentContact, currentDistributionList)

      return state.setIn(['contactDetailsData', 'contact'], normalizeContact(mergedContact))
    },
    [Actions.fetchEmailEventsSuccess]: (state, { payload }) => state
      .setIn(['contactDetailsData', 'emailEvents'], fromJS(payload.emailEvents)),
    [Actions.fetchEarnedMediaSuccess]: (state, { payload }) => state
      .setIn(['contactDetailsData', 'earnedMedia'], fromJS(payload.earnedMedia)),
    [Actions.fakeUpdateContact]: (state, { payload }) => applyFakeContactAttributes(state, payload),
    [Actions.revertFakeUpdateContact]: (state, { payload }) => revertFakeContactUpdate(state, payload),

    [Actions.createDistributionListSuccess]: (state, { payload }) => {
      const distLists = state.get('distributionLists')
      const newDistList = fromJS({ ...payload, contacts: fromJS([]) })

      return state
        .set('distributionLists', distLists.unshift(newDistList))
        .set('distributionListsTotal', state.get('distributionListsTotal') + 1)
    },
    [Actions.deleteDistributionListsSuccess]: (state, { payload: deletedListsIds }) => {
      const newDistributionLists = state.get('distributionLists').filter(dl => !deletedListsIds.includes(dl.get('id')))

      return state.merge({
        distributionLists: newDistributionLists,
        distributionListsTotal: state.get('distributionListsTotal') - deletedListsIds.length,
        selectedDistributionLists: initialState.get('selectedDistributionLists')
      })
    },
    [combineActions(
      Actions.addContactsToDistributionListSuccess
    )]: (state, { payload: { result, contactIds } }) => {
      const { newLists = [], lists = [] } = result

      let contacts = state.get('contacts')
      let distributionLists = state.get('distributionLists')
      let distributionListsTotal = state.get('distributionListsTotal')
      let selectedList = state.getIn(['distributionListDetailsData', 'list'])

      if (lists.length > 0) {
        if (selectedList.get('id')) {
          selectedList = fromJS(lists[0])
        }

        lists.forEach(dl => {
          const elemIndex = distributionLists.findIndex(l => l.get('id') === dl.id)

          if (elemIndex !== -1) {
            distributionLists = distributionLists.set(elemIndex, fromJS(dl))
          }
        })
      }

      if (newLists.length > 0) {
        distributionLists = fromJS(newLists).concat(distributionLists).slice(0, 10)
        distributionListsTotal += newLists.length
      }

      contacts = contacts.map(contact => {
        if (!contactIds.includes(contact.get('id'))) {
          return contact
        }

        let contactLists = contact.get('distributionLists')

        if (newLists.length > 0) {
          const newListRefs = fromJS(newLists.map(nl => ({ id: nl.id, name: nl.name })))
          contactLists = newListRefs.concat(contactLists)
        }

        if (lists.length > 0) {
          lists.forEach(dl => {
            const exists = contactLists.some(l => l.get('id') === dl.id)

            if (!exists) {
              contactLists = contactLists.unshift(fromJS({ id: dl.id, name: dl.name }))
            }
          })
        }

        return contact.set('distributionLists', contactLists)
      })

      return state
        .set('contacts', contacts)
        .set('distributionLists', distributionLists)
        .set('distributionListsTotal', distributionListsTotal)
        .setIn(['distributionListDetailsData', 'list'], selectedList)
        .setIn(['distributionListDetailsData', 'selectedContactsToAdd'],
          initialState.getIn(['distributionListDetailsData', 'selectedContactsToAdd']))
        .set('distributionListFormData', initialState.get('distributionListFormData'))
    },
    [Actions.updateDistributionListSuccess]: (state, { payload }) => {
      const newDistributionLists = state.get('distributionLists').map(dl => (dl.get('id') === payload.id ? fromJS(payload) : dl))
      let newSelectedDistributionList = state.getIn(['distributionListDetailsData', 'list'])

      if (newSelectedDistributionList.get('id')) {
        newSelectedDistributionList = fromJS(payload)
      }

      return state.set('distributionLists', newDistributionLists)
        .setIn(['distributionListDetailsData', 'list'], newSelectedDistributionList)
    },
    [Actions.openDistributionListDetails]: state => state
      .set('selectedDistributionLists', fromJS([])),

    [Actions.closeDetailPages]: state => state
      .set('distributionListDetailsData', initialState.get('distributionListDetailsData'))
      .setIn(['distributionListDetailsData', 'selectedContactsToAdd'],
        initialState.getIn(['distributionListDetailsData', 'selectedContactsToAdd'])),
    [Actions.clearDetailPage]: state => state.set('contactDetailsData', initialState.get('contactDetailsData')),

    [Actions.openContactRemoveDialog]: (state, { payload }) => {
      if (payload) {
        return state.setIn(['distributionListDetailsData', 'contactsToRemove'], fromJS(payload))
      }

      return state
    },
    [Actions.closeContactRemoveDialog]: state => state
      .setIn(['distributionListDetailsData', 'contactsToRemove'], initialState.getIn(['distributionListDetailsData', 'contactsToRemove'])),

    [Actions.closeContactsAddDialog]: state => state.set('distributionListFormData', initialState.get('distributionListFormData')),
    [Actions.updateDistributionListFormData]: (state, { payload: { key, value } }) => state
      .setIn(['distributionListFormData', key], fromJS(value)),

    [Actions.selectContactToAdd]: (state, { payload: contactData }) => {
      const contactIds = state.getIn(['distributionListDetailsData', 'selectedContactsToAdd']).map(contact => contact.get('id'))

      if (contactIds.includes(contactData.get('id'))) {
        return state.updateIn(['distributionListDetailsData', 'selectedContactsToAdd'],
          contacts => contacts.filter(contact => contact.get('id') !== contactData.get('id')))
      }

      return state.updateIn(['distributionListDetailsData', 'selectedContactsToAdd'], contacts => contacts.push(contactData))
    },

    [Actions.selectDistributionListContact]: (state, { payload: contactData }) => {
      const contactIds = state.getIn(['distributionListDetailsData', 'selectedContactsToRemove']).map(contact => contact.get('id'))

      if (contactIds.includes(contactData.get('id'))) {
        return state.updateIn(['distributionListDetailsData', 'selectedContactsToRemove'],
          contacts => contacts.filter(contact => contact.get('id') !== contactData.get('id')))
      }

      return state.updateIn(['distributionListDetailsData', 'selectedContactsToRemove'], contacts => contacts.push(contactData))
    },

    [Actions.selectAllContactsToAdd]: (state, { payload }) => {
      const selectedContacts = state.getIn(['distributionListDetailsData', 'selectedContactsToAdd'])

      const newSelectedContacts = fromJS(payload)
        .filter(contact => !selectedContacts.map(c => c.get('id')).includes(contact.get('id')))

      if (newSelectedContacts.isEmpty()) {
        return state.setIn(['distributionListDetailsData', 'selectedContactsToAdd'],
          initialState.getIn(['distributionListDetailsData', 'selectedContactsToAdd']))
      }

      return state.updateIn(['distributionListDetailsData', 'selectedContactsToAdd'],
        contacts => contacts.concat(newSelectedContacts))
    },

    [Actions.selectAllDistributionListContacts]: (state, { payload }) => {
      const selectedContacts = state.getIn(['distributionListDetailsData', 'selectedContactsToRemove'])

      const newSelectedContacts = fromJS(payload)
        .filter(contact => !selectedContacts.map(c => c.get('id')).includes(contact.get('id')))

      if (newSelectedContacts.isEmpty()) {
        return state.setIn(['distributionListDetailsData', 'selectedContactsToRemove'],
          initialState.getIn(['distributionListDetailsData', 'selectedContactsToRemove']))
      }

      return state.updateIn(['distributionListDetailsData', 'selectedContactsToRemove'],
        contacts => contacts.concat(newSelectedContacts))
    },

    [Actions.setSelectedDistributionListContacts]: (state, { payload }) => state.setIn(
      ['distributionListDetailsData', 'selectedContactsToRemove'],
      ImmutableList.isList(payload) ? payload : fromJS(payload || [])
    ),

    [Actions.clearContactsToAdd]: state => state.setIn(['distributionListDetailsData', 'selectedContactsToAdd'],
      initialState.getIn(['distributionListDetailsData', 'selectedContactsToAdd'])),

    [Actions.clearDistributionListContacts]: state => state.setIn(['distributionListDetailsData', 'selectedContactsToRemove'],
      initialState.getIn(['distributionListDetailsData', 'selectedContactsToRemove'])),

    [Actions.removeContactsFromListSuccess]: (state, { payload: { result, contactIds } }) => {
      let contacts = state.get('contacts')

      contacts = contacts.map(contact => {
        if (!contactIds.includes(contact.get('id'))) {
          return contact
        }

        let contactLists = contact.get('distributionLists')

        contactLists = contactLists.filter(
          dl => dl.get('id') !== result.id
        )

        return contact.set('distributionLists', contactLists)
      })

      const elemIndex = state
        .get('distributionLists')
        .findIndex(dl => dl.get('id') === result.id)

      return state
        .setIn(['distributionLists', elemIndex], fromJS(result))
        .setIn(['distributionListDetailsData', 'list'], fromJS(result))
        .updateIn(['distributionListDetailsData', 'selectedContactsToRemove'],
          sc => sc.filter(c => !contactIds.includes(c.get('id'))))
        .setIn(['distributionListDetailsData', 'contactsToRemove'], initialState.getIn(['distributionListDetailsData', 'contactsToRemove']))
        .set('contacts', contacts)
    },

    [Actions.removeContactsFromDistributionListsSuccess]: (state, { payload: { lists, contactIds } }) => {
      const listIds = lists.map(list => list.id)
      const contacts = state.get('contacts').map(contact => {
        if (!contactIds.includes(contact.get('id'))) {
          return contact
        }

        return contact.update('distributionLists', contactLists => contactLists.filter(
          list => !listIds.includes(list.get('id'))
        ))
      })
      const distributionLists = state.get('distributionLists').map(list => {
        const updatedList = lists.find(updated => updated.id === list.get('id'))

        return updatedList ? fromJS(updatedList) : list
      })

      return state
        .set('contacts', contacts)
        .set('distributionLists', distributionLists)
    },

    [Actions.setDistributionListsDataGridSettings]: (state, { payload: { key, value } }) => {
      if (key !== 'rowSelectionModel') {
        return state
      }

      return state.set('selectedDistributionLists', getDistributionListsByIds(state, value))
    },

    [Actions.selectDistributionList]: (state, { payload: distributionList }) => {
      const selectedLists = state.get('selectedDistributionLists')
      const id = distributionList.get('id')

      if (selectedLists.findIndex(dl => dl.get('id') === id) !== -1) {
        const newSelectedLists = selectedLists.filter(dl => dl.get('id') !== id)

        return state.set('selectedDistributionLists', newSelectedLists)
      }

      return state.set('selectedDistributionLists', selectedLists.push(distributionList))
    },

    [Actions.clearSelectedDistributionLists]: state => (
      state.set('selectedDistributionLists', initialState.get('selectedDistributionLists'))
    ),

    [Actions.searchDistributionListSuccess]: (state, { payload: { distributionLists } }) => (
      state.set('distributionListsSearch', fromJS(distributionLists))),

    [Actions.searchContactsSuccess]: (state, { payload: { contacts } }) => (
      state.set('contactsSearch', fromJS(contacts))),

    [Actions.setSelectedMainTab]: (state, { payload }) => {
      // Reset selection if leaving 'distribution lists' tab
      if (payload === 'contacts') {
        return state.set('selectedDistributionLists', initialState.get('selectedDistributionLists'))
      }

      return state
    },

    [Actions.setSelectedListTab]: (state, { _payload }) => (
      state
        .setIn(['distributionListDetailsData', 'selectedContactsToAdd'], fromJS([]))
        .setIn(['distributionListDetailsData', 'selectedContactsToRemove'], fromJS([]))
    ),

    [Actions.mergeDistributionListsSuccess]: (state, { payload: { mergedList, deletedListsIds } }) => {
      const distributionLists = state.get('distributionLists').filter(dl => !deletedListsIds.includes(dl.get('id')))

      return state
        .set('distributionLists', distributionLists.unshift(fromJS(mergedList)))
        .set('selectedDistributionLists', initialState.get('selectedDistributionLists'))
        .set('distributionListsTotal', state.get('distributionListsTotal') + 1 - deletedListsIds.length)
    },

    // Contact Creation
    [Actions.createContactSuccess]: (state, { payload }) => {
      let intermediateState = state

      if (payload.editAfterCreate) {
        intermediateState = intermediateState.set('contactDetailsData', fromJS({ contact: normalizeContact(payload.contact) }))
      }

      return intermediateState
        .set('newContactData', normalizeContact(payload.contact))
        .set('contacts', state.get('contacts').unshift(normalizeContact(payload.contact)))
    },
    [Actions.setSuggestedContactData]: (state, { payload }) => state.set('suggestedContactData', fromJS(payload)),
    [Actions.importSuggestedContactSuccess]: (state, { payload }) => {
      let intermediateState = state

      if (payload.editAfterCreate) {
        intermediateState = intermediateState.set('contactDetailsData', fromJS({ contact: normalizeContact(payload.contact) }))
      }

      return intermediateState
        .set('newContactData', normalizeContact(payload.contact))
        .set('contacts', state.get('contacts').unshift(normalizeContact(payload.contact)))
    },

    // Contact Update/Reset/Report
    [combineActions(
      Actions.updateContactSuccess,
      Actions.resetContactSuccess,
      Actions.sendErrorReportSuccess
    )]: (state, { payload }) => {
      const newContact = normalizeContact(payload)
      const newContactId = newContact.get('id')
      const updatedContacts = state.get('contacts').map(contact => (contact.get('id') === newContactId ? newContact : contact))

      return state
        .set('contacts', updatedContacts)
        .setIn(['contactDetailsData', 'contact'], newContact)
    },
    [Actions.clearContacts]: state => state.set('contacts', initialState.get('contacts'))
      .set('contactsTotal', initialState.get('contactsTotal')),

    [combineActions(
      Actions.resetState,
      AppActions.resetState
    )]: () => initialState
  }, initialState
)
